import axios from 'axios';

import type { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';
import type { TimetableTripGroupEntity, TimetableTripGroupsEntity } from 'src/entities/Timetable.entity';
import type { ApplyTimetableResult, ApplyTimetableWarning } from 'src/hooks/useConvenienceDispatchMutation';

export type NeedsReviewCandidate = { truckId: number; rotationNo: number; courseId: number; courseCode: string };

/**
 * 店舗名 → 候補（便グループの sortOrder 順、便グループごとに最初の訪問 1 つ）。要確認の店舗だけを持つ。
 * API の Timetables::CourseStopIndex と同じ規則（トラック無しは数えない・同じ便グループの 2 回目以降は数えない）
 */
export function computeNeedsReviewStores(
  tripGroups: TimetableTripGroupEntity[] | null | undefined,
): Map<string, NeedsReviewCandidate[]> {
  const byStore = new Map<string, NeedsReviewCandidate[]>();
  if (!tripGroups) return byStore;
  const groups = [...tripGroups].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  for (const g of groups) {
    if (g.truckId == null) continue; // 反映で飛ばされ、置くこともできない便グループは数えない（API と同じ）
    const tripByNo = new Map(g.trips.map((t) => [t.tripNo, t]));
    const stops = g.stops
      .filter((s) => s.stopType === 'unloading' && tripByNo.has(s.rotationNo))
      .sort((a, b) => a.rotationNo - b.rotationNo || a.sequenceNo - b.sequenceNo);
    const seen = new Set<string>();
    for (const s of stops) {
      if (seen.has(s.placeName)) continue; // 同じ便グループの 2 回目以降の訪問は数えない（L10 (c)）
      seen.add(s.placeName);
      const trip = tripByNo.get(s.rotationNo);
      const list = byStore.get(s.placeName) ?? [];
      list.push({ truckId: g.truckId, rotationNo: s.rotationNo, courseId: trip.courseId, courseCode: trip.courseCode });
      byStore.set(s.placeName, list);
    }
  }
  for (const [name, list] of byStore) if (list.length < 2) byStore.delete(name);
  return byStore;
}

/**
 * 読み手（vehicleRowVM / needsReview）に渡す便グループ。未選択・初回取得中・取得失敗は null で、要確認・本日なし・ダイヤ外を出さない。
 * Why not data だけを見る: TanStack は取り直しに失敗しても前の data を残すので、消えたダイヤ（404）の便グループで判定し続ける
 */
export function selectTripGroups(
  timetableId: number | null,
  query: { data?: TimetableTripGroupsEntity; isError: boolean },
): TimetableTripGroupEntity[] | null {
  if (timetableId == null || query.isError) return null;
  return query.data?.tripGroups ?? null;
}

/**
 * 置いた leg run のうち、要確認・置き先トラックが候補・候補のコースがまだ付いていない（別コースの付け替えを含む）ものの id。
 * Why not コードで比べる: 同じコードで別の便（2011 の 1 便と、101 に紐付いた 2011 の 2 便）が別の便グループに並ぶと付け替えを落とす
 */
export function pickFixedCourseAssignmentLegRunIds(
  legRuns: ShipmentOrderLegRunForPlanEntity[],
  truckId: number | null | undefined,
  needsReview: Map<string, NeedsReviewCandidate[]>,
): number[] {
  if (truckId == null) return [];
  return legRuns
    .filter((lr) => {
      if (lr.id == null) return false;
      const candidate = (needsReview.get(lr.unloading_name) ?? []).find((c) => c.truckId === truckId);
      return candidate != null && candidate.courseId !== lr.fixed_course_id;
    })
    .map((lr) => lr.id);
}

/** 未割当の伝票に付ける要確認バッジの候補。固定コースが既に付いていれば付けない（置き直しの対象でもない） */
export function needsReviewCandidatesFor(
  fixedCourseId: number | null | undefined,
  storeName: string,
  needsReview: Map<string, NeedsReviewCandidate[]>,
): NeedsReviewCandidate[] | undefined {
  return fixedCourseId == null ? needsReview.get(storeName) : undefined;
}

/** 他車両へ配車の移動先（shiftId）から、固定コースの候補と突き合わせる truckId を引く */
export function findTruckIdByShiftId(
  deliveries: { shiftId: number; truckId: number }[],
  shiftId: number,
): number | null {
  return deliveries.find((d) => d.shiftId === shiftId)?.truckId ?? null;
}

export type ApplyNotification = { message: string; variant: 'success' | 'warning' };

const MAX_STORES_IN_NOTIFICATION = 3;

export type ApplyNotificationInput = Pick<ApplyTimetableResult, 'assignedLegRunCount' | 'needsReviewStores'>
  & { warnings?: ApplyTimetableWarning[] };

export function buildApplyNotifications(result: ApplyNotificationInput, mode: 'call' | 'align'): ApplyNotification[] {
  const head = mode === 'align'
    ? `ダイヤに対応づけました（${result.assignedLegRunCount} 件を割り当て）`
    : 'ダイヤを呼び出しました';
  const notReason = mode === 'align' ? '割り当てていません' : '固定コースを付けていません';
  const stores = result.needsReviewStores;
  const shown: ApplyNotification[] = stores.slice(0, MAX_STORES_IN_NOTIFICATION).map((s) => ({
    message: `${s.storeName} の伝票 ${s.legRunCount} 件は、複数のコース（${s.candidates.map((c) => c.courseCode).join('・')}）に入っているため${notReason}`,
    variant: 'warning',
  }));
  const rest = stores.slice(MAX_STORES_IN_NOTIFICATION);
  if (rest.length > 0) {
    const count = rest.reduce((sum, s) => sum + s.legRunCount, 0);
    shown.push({ message: `ほか ${rest.length} 店舗の伝票 ${count} 件も複数のコースに入っているため${notReason}`, variant: 'warning' });
  }
  // トラックの無い便グループは反映で飛ぶ（画面に空のトラックすら出ないので知らせる。#363 決定 4）
  const truckMissing: ApplyNotification[] = (result.warnings ?? [])
    .filter((w) => w.code === 'truck_missing')
    .map((w) => ({ message: `コース ${w.courseCode} の ${w.tripNo} 便はトラックが無いため飛ばしました`, variant: 'warning' }));
  return [{ message: head, variant: 'success' }, ...shown, ...truckMissing];
}

/**
 * 反映 API の失敗を snackbar の文言にする。404 は /courses で消されたダイヤなので、呼び出し側が一覧を取り直す印を返す。
 * 409（lock_conflict）・422 は API の error をそのまま（やり直しを促す文言は API が持つ）
 */
export function describeApplyError(error: unknown, mode: 'call' | 'align'): { message: string; notFound: boolean } {
  const fallback = mode === 'call' ? 'ダイヤを呼び出せませんでした' : 'ダイヤに対応づけできませんでした';
  if (!axios.isAxiosError(error)) return { message: fallback, notFound: false };
  const status = error.response?.status;
  if (status === 404) return { message: 'ダイヤが見つかりません', notFound: true };
  const body = error.response?.data as { error?: unknown } | undefined;
  if ((status === 409 || status === 422) && typeof body?.error === 'string') return { message: body.error, notFound: false };
  return { message: fallback, notFound: false };
}
